import { resolveSpxValue } from '@wix/thunderbolt-commons'
import _ from 'lodash'

import { Spx } from '@wix/thunderbolt-becky-types'

import {
	MathFunction,
	Percentage,
	KeywordType,
	Number,
	Length,
	Angle,
	MathFunctionValue,
	MathOperation,
	LineHeight,
} from './type'

/** Converters */

const mathValueToResolvedString = (prop: MathFunctionValue | MathOperation, spx?: Spx) => {
	switch (prop.type) {
		case 'Operation':
			return mathOperationConverter(prop)
		case 'spx':
			return spxConverter(prop, spx)
		default:
			return mathFunctionValuesTypeToConverter[prop.type](prop)
	}
}

export const mathFunctionCommonConverter = (styleProp: MathFunction, spx?: Spx): string => {
	const convertedValues = styleProp.values.map((functionValue: MathFunctionValue) => {
		return mathValueToResolvedString(functionValue, spx)
	})
	const joinSeparator = styleProp.function === 'calc' ? ' ' : ','
	return `${styleProp.function}(${convertedValues.join(joinSeparator)})`
}

export const keyWordConverter = (prop: KeywordType<string>): string => {
	return prop.value
}
export const numberConverter = (prop: Number): string => {
	return `${prop.value}`
}

export const percentageConverter = (prop: Percentage): string => {
	return `${prop.value}%`
}

export const spxConverter = (prop: Length, spx?: Spx): string => {
	return resolveSpxValue(`${prop.value}spx`, spx)
}

export const commonAngleConverter = (prop: Angle): string => {
	return `${prop.value}${prop.type}`
}

export const commonLengthConverter = (prop: Length): string => {
	return `${prop.value}${prop.type}`
}

export const mathOperationConverter = (styleProp: MathOperation): string => {
	return `${styleProp.value}`
}

export const mathFunctionConverter = (styleProp: MathFunction, spx?: Spx): string => {
	return mathFunctionTypeToConverter[styleProp.type](styleProp, spx)
}

export const lineHeightConverter = (styleProp: LineHeight, spx?: Spx): string => {
	return lineHeightTypeToConverter[styleProp.type](styleProp, spx)
}

/** Maps */

const mathFunctionTypeToConverter = {
	MathFunction: mathFunctionCommonConverter,
}

export const lengthPercentageTypeToConverter = {
	spx: spxConverter,
	px: commonLengthConverter,
	pt: commonLengthConverter,
	pc: commonLengthConverter,
	in: commonLengthConverter,
	cm: commonLengthConverter,
	mm: commonLengthConverter,
	em: commonLengthConverter,
	rem: commonLengthConverter,
	vw: commonLengthConverter,
	vh: commonLengthConverter,
	vmin: commonLengthConverter,
	vmax: commonLengthConverter,
}

const angleTypeToConverter = {
	deg: commonAngleConverter,
	rad: commonAngleConverter,
	grad: commonAngleConverter,
	turn: commonAngleConverter,
}

type BasicStyleProps = Number | KeywordType<string> | Percentage | Length
type StylePropType = BasicStyleProps['type'] // Extract the types

export const basicStyleConvertersMap = _.mergeWith(
	{
		number: numberConverter,
		Keyword: keyWordConverter,
		percentage: percentageConverter,
	},
	lengthPercentageTypeToConverter,
	angleTypeToConverter
) as Record<StylePropType, (prop: BasicStyleProps, spx?: Spx) => string>

const mathFunctionValuesTypeToConverter = _.mergeWith(
	{
		MathFunction: mathFunctionCommonConverter,
		Keyword: keyWordConverter,
		number: numberConverter,
	},
	basicStyleConvertersMap
) as Record<string, (prop: MathFunctionValue | MathOperation, spx?: Spx) => string>

const lineHeightTypeToConverter = _.mergeWith(
	{
		Keyword: keyWordConverter,
		number: numberConverter,
	},
	lengthPercentageTypeToConverter
) as Record<string, (prop: LineHeight, spx?: Spx) => string>
